import { useEffect, useState } from "react";
import { Button, Checkbox, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from "@nextui-org/react";
import ReactSelect from "react-select";
import useSWR from "swr";
import toast from "react-hot-toast";
import { EyeIcon, PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { getFetcher, postFetcher, putFetcher, putFetcherWithoutId } from "../../../../services/api";
import { isResponseErrorObject } from "../../../../services/helpers";

// Urutan, judul dan awalan nama mengikuti api/src/models/enums/kategoriDaftarTembusan.ts:
// kategori master tembusan ditentukan dari awalan namanya.
const BAGIAN = [
  {
    kategori: "GUBERNUR",
    judul: "Daftar Gubernur",
    field: "gubernurIds",
    contohNama: "Gubernur Provinsi ... up Kepala Dinas Lingkungan Hidup Provinsi ...",
    awalan: /^\s*gubernur\s/i,
    pesanAwalan: 'Nama harus diawali "Gubernur"',
  },
  {
    kategori: "BUPATI_WALIKOTA",
    judul: "Daftar Bupati / Walikota",
    field: "bupatiWalikotaIds",
    contohNama: "Walikota ... up Kepala Dinas Lingkungan Hidup Kota ...",
    awalan: /^\s*(bupati|wali\s*kota)\s/i,
    pesanAwalan: 'Nama harus diawali "Bupati" atau "Walikota"',
  },
  {
    kategori: "KEPALA_KANTOR_BEA_CUKAI",
    judul: "Daftar Kepala Kantor Pelayanan Utama Bea dan Cukai",
    field: "kepalaKantorBeaCukaiIds",
    contohNama: "Kepala Kantor Pelayanan Utama Bea dan Cukai ...",
    awalan: /^\s*kepala kantor.*\sbea\s/i,
    pesanAwalan: 'Nama harus diawali "Kepala Kantor" dan memuat "Bea"',
  },
];

const DAFTAR_KOSONG = { GUBERNUR: [], BUPATI_WALIKOTA: [], KEPALA_KANTOR_BEA_CUKAI: [] };

const keOpsi = (items) => (items || []).map((item) => ({ value: item.id, label: item.nama }));

// Sama dengan PDF: butir diakhiri titik koma, butir terakhir tiap bagian diakhiri titik.
const namaBerakhiran = (nama, isLast) => `${nama.trim().replace(/[;.]+$/, "")}${isLast ? "." : ";"}`;

function tampilkanError(error, pesanDefault) {
  const message = error?.response?.data?.message;
  if (isResponseErrorObject(message)) {
    Object.values(message).forEach((value) => toast.error(value));
  } else if (Array.isArray(message)) {
    message.forEach((value) => toast.error(value));
  } else {
    toast.error(message || pesanDefault);
  }
}

function BagianTembusan({ bagian, items, onChange, isEditable, isOpen, onTambahMaster, onEditMaster }) {
  const { data: pilihan, isLoading } = useSWR(isOpen ? `/api/registrasi/daftar-tembusan/pilihan?kategori=${bagian.kategori}` : null, getFetcher);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  const handleDragEnter = (index) => {
    if (draggedItemIndex === null || draggedItemIndex === index) return;
    const urutan = Array.from(items);
    const [dipindah] = urutan.splice(draggedItemIndex, 1);
    urutan.splice(index, 0, dipindah);
    setDraggedItemIndex(index);
    onChange(urutan);
  };

  return (
    <div className="flex flex-col gap-2 rounded-medium border border-default-200 p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">{bagian.judul}</p>
        <Button isIconOnly size="sm" color="primary" onPress={() => onTambahMaster(bagian)} isDisabled={!isEditable} aria-label={`Tambah master ${bagian.judul}`}>
          <PlusIcon className="size-4" />
        </Button>
      </div>
      <ReactSelect
        isMulti
        isDisabled={!isEditable}
        placeholder={`Pilih ${bagian.judul.replace("Daftar ", "")}...`}
        options={keOpsi(pilihan)}
        isLoading={isLoading}
        value={items}
        onChange={(options) => onChange(options || [])}
        menuPortalTarget={document.body}
        menuPlacement="auto"
        noOptionsMessage={() => "Tidak ada data"}
        styles={{
          control: (provided) => ({ ...provided, fontSize: "14px" }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (provided) => ({ ...provided, maxHeight: "200px", overflowY: "auto" }),
        }}
      />
      <Table removeWrapper aria-label={`tabel-${bagian.kategori}`}>
        <TableHeader>
          <TableColumn width={40}>No</TableColumn>
          <TableColumn>Tembusan</TableColumn>
          <TableColumn width={90}>Actions</TableColumn>
        </TableHeader>
        <TableBody emptyContent="Belum ada tembusan">
          {items.map((item, index) => (
            <TableRow
              key={item.value}
              style={{ cursor: isEditable ? "grab" : "default" }}
              draggable={isEditable}
              onDragStart={() => setDraggedItemIndex(index)}
              onDragEnter={() => handleDragEnter(index)}
              onDragEnd={() => setDraggedItemIndex(null)}
            >
              <TableCell>{index + 1}</TableCell>
              <TableCell className="text-sm">{item.label}</TableCell>
              <TableCell className="flex items-center gap-1">
                <Button size="sm" color="warning" isIconOnly isDisabled={!isEditable} onPress={() => onEditMaster(bagian, item)} aria-label="Edit tembusan">
                  <PencilSquareIcon className="size-4" />
                </Button>
                <Button size="sm" color="danger" isIconOnly isDisabled={!isEditable} onPress={() => onChange(items.filter((x) => x.value !== item.value))} aria-label="Hapus dari daftar">
                  <TrashIcon className="size-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function LampiranTembusan({ registrasiId, data, mutate, isEditable }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [daftar, setDaftar] = useState(DAFTAR_KOSONG);
  const [formMaster, setFormMaster] = useState(null); // { bagian, item?, nama }
  const [isSavingMaster, setIsSavingMaster] = useState(false);

  const isChecked = !!data?.lampiran_tembusan;

  const { data: tersimpan, isLoading, mutate: mutateTersimpan } = useSWR(isOpen ? `/api/registrasi/${registrasiId}/daftar-tembusan` : null, getFetcher, {
    revalidateOnFocus: false,
  });

  // Isi ulang form dari data tersimpan setiap kali modal dibuka.
  useEffect(() => {
    if (isOpen && tersimpan) {
      setDaftar({
        GUBERNUR: keOpsi(tersimpan.GUBERNUR),
        BUPATI_WALIKOTA: keOpsi(tersimpan.BUPATI_WALIKOTA),
        KEPALA_KANTOR_BEA_CUKAI: keOpsi(tersimpan.KEPALA_KANTOR_BEA_CUKAI),
      });
    }
  }, [isOpen, tersimpan]);

  const jumlahTembusan = BAGIAN.reduce((total, bagian) => total + daftar[bagian.kategori].length, 0);

  const onToggle = async (checked) => {
    if (checked) {
      // Baru tersimpan saat SIMPAN di modal; bila modal ditutup, centang kembali kosong.
      setIsOpen(true);
      return;
    }
    try {
      await putFetcherWithoutId(`/api/registrasi/${registrasiId}/daftar-tembusan`, { lampiran_tembusan: false });
      await mutate();
      toast.success("Lampiran Tembusan tidak dicetak. Daftarnya tetap tersimpan.");
    } catch (error) {
      tampilkanError(error, "Gagal mengubah Lampiran Tembusan");
    }
  };

  const onSimpan = async () => {
    if (jumlahTembusan === 0) {
      toast.error("Pilih minimal satu tembusan");
      return;
    }
    setIsSaving(true);
    try {
      const payload = { lampiran_tembusan: true };
      BAGIAN.forEach((bagian) => {
        payload[bagian.field] = daftar[bagian.kategori].map((item) => item.value);
      });
      await putFetcherWithoutId(`/api/registrasi/${registrasiId}/daftar-tembusan`, payload);
      await Promise.all([mutate(), mutateTersimpan()]);
      toast.success("Lampiran Tembusan berhasil disimpan!");
      setIsOpen(false);
    } catch (error) {
      tampilkanError(error, "Gagal menyimpan Lampiran Tembusan");
    } finally {
      setIsSaving(false);
    }
  };

  const onSimpanMaster = async () => {
    const { bagian, item, nama } = formMaster;
    const namaBersih = nama.trim();
    if (!bagian.awalan.test(namaBersih)) {
      toast.error(bagian.pesanAwalan);
      return;
    }
    setIsSavingMaster(true);
    try {
      if (item) {
        if (namaBersih !== item.label) {
          await putFetcher("/api/data-master/tembusan", item.value, { nama: namaBersih });
        }
        setDaftar((prev) => ({
          ...prev,
          [bagian.kategori]: prev[bagian.kategori].map((x) => (x.value === item.value ? { value: x.value, label: namaBersih } : x)),
        }));
        toast.success("Tembusan berhasil diubah!");
      } else {
        // Tipe PEJABAT DAERAH: tipe yang sudah dipakai untuk entri gubernur/bupati/walikota di master.
        const baru = await postFetcher("/api/data-master/tembusan", { nama: namaBersih, tipe: "PEJABAT DAERAH" });
        setDaftar((prev) => ({
          ...prev,
          [bagian.kategori]: [...prev[bagian.kategori], { value: baru.id, label: namaBersih }],
        }));
        toast.success("Tembusan berhasil ditambah!");
      }
      setFormMaster(null);
    } catch (error) {
      tampilkanError(error, "Gagal menyimpan tembusan");
    } finally {
      setIsSavingMaster(false);
    }
  };

  const bagianTerisi = BAGIAN.filter((bagian) => daftar[bagian.kategori].length > 0);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Checkbox isSelected={isChecked} onValueChange={onToggle} isDisabled={!isEditable} size="sm">
          <span className="text-sm">Lampiran Tembusan</span>
        </Checkbox>
        {isChecked && (
          <Button size="sm" variant="flat" color="primary" startContent={isEditable ? <PencilSquareIcon className="size-4" /> : <EyeIcon className="size-4" />} onPress={() => setIsOpen(true)}>
            {isEditable ? "Ubah Daftar Tembusan" : "Lihat Daftar Tembusan"}
          </Button>
        )}
      </div>

      <Modal isOpen={isOpen} onOpenChange={setIsOpen} size="4xl" scrollBehavior="inside" isDismissable={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Lampiran Tembusan
                <span className="text-sm font-normal text-default-500">Dicetak sebagai halaman DAFTAR TEMBUSAN pada draft SK. Urutan bisa diubah dengan menyeret baris tabel.</span>
              </ModalHeader>
              <ModalBody>
                {isLoading ? (
                  <Spinner />
                ) : (
                  BAGIAN.map((bagian) => (
                    <BagianTembusan
                      key={bagian.kategori}
                      bagian={bagian}
                      items={daftar[bagian.kategori]}
                      onChange={(items) => setDaftar((prev) => ({ ...prev, [bagian.kategori]: items }))}
                      isEditable={isEditable}
                      isOpen={isOpen}
                      onTambahMaster={(b) => setFormMaster({ bagian: b, item: null, nama: "" })}
                      onEditMaster={(b, item) => setFormMaster({ bagian: b, item, nama: item.label })}
                    />
                  ))
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" color="secondary" startContent={<EyeIcon className="size-4" />} onPress={() => setIsPreviewOpen(true)} isDisabled={jumlahTembusan === 0}>
                  Preview
                </Button>
                {isEditable && (
                  <Button color="primary" onPress={onSimpan} isLoading={isSaving} isDisabled={isSaving || isLoading}>
                    Simpan
                  </Button>
                )}
                <Button color="danger" variant="flat" onPress={onClose} isDisabled={isSaving}>
                  {isEditable ? "Batal" : "Tutup"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Preview: tampilan halaman DAFTAR TEMBUSAN, hanya tombol TUTUP */}
      <Modal isOpen={isPreviewOpen} onOpenChange={setIsPreviewOpen} size="3xl" scrollBehavior="inside">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Preview Daftar Tembusan</ModalHeader>
              <ModalBody>
                <div className="rounded-medium border border-default-200 bg-white p-6 text-black" style={{ fontFamily: "Arial, sans-serif", fontSize: "10pt" }}>
                  <p className="text-center font-bold">DAFTAR TEMBUSAN</p>
                  <p className="mb-5 text-center font-bold">{data?.company?.name}</p>
                  <ol className="pl-6" style={{ listStyleType: "upper-alpha" }}>
                    {bagianTerisi.map((bagian) => (
                      <li key={bagian.kategori} className="mb-3">
                        {bagian.judul}
                        <ol className="mt-1 pl-6" style={{ listStyleType: "decimal" }}>
                          {daftar[bagian.kategori].map((item, index) => (
                            <li key={item.value}>{namaBerakhiran(item.label, index === daftar[bagian.kategori].length - 1)}</li>
                          ))}
                        </ol>
                      </li>
                    ))}
                  </ol>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="primary" onPress={onClose}>
                  TUTUP
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Tambah / edit master tembusan untuk satu bagian */}
      <Modal isOpen={!!formMaster} onOpenChange={(open) => !open && setFormMaster(null)}>
        <ModalContent>
          <ModalHeader>
            {formMaster?.item ? "Edit" : "Tambah"} {formMaster?.bagian?.judul?.replace("Daftar ", "")}
          </ModalHeader>
          <ModalBody>
            <Input
              label="Nama"
              isRequired
              value={formMaster?.nama ?? ""}
              onValueChange={(nama) => setFormMaster((prev) => ({ ...prev, nama }))}
              placeholder={formMaster?.bagian?.contohNama}
              description={formMaster?.item ? "Mengubah nama di master tembusan (ikut berubah di SK lain yang memakainya)" : formMaster?.bagian?.pesanAwalan}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onPress={onSimpanMaster} isLoading={isSavingMaster} isDisabled={!formMaster?.nama?.trim()}>
              {formMaster?.item ? "Simpan" : "Tambah"}
            </Button>
            <Button color="danger" onPress={() => setFormMaster(null)} isDisabled={isSavingMaster}>
              Batal
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
